/**
 * 하이라이터 좌표 계산 — 레거시 `/api/prescription/highlightGeometry` 클라이언트 포팅.
 * analysisPipeline은 fs 의존이 없어 브라우저에서 동일하게 실행한다.
 * ocrRawUrl은 S3 CORS 이슈가 있을 수 있어 실패 시 document 재OCR 경로를 쓴다.
 */
import { buildAnalysisRows } from '@/shared/lib/highlighter/analysisPipeline';

export interface GeometryDrugInput {
  drugCode?: string | null;
  drugName?: string | null;
  pharmaCompanyName?: string | null;
  unitPrice?: number | null;
  outQuantity?: number | null;
  inQuantity?: number | null;
  totalAmount?: number | null;
}

export interface HighlightGeometryVertex {
  x: number;
  y: number;
}

export interface HighlightGeometryRow {
  rowId: string;
  rawDrugName?: string | null;
  rawDrugCode?: string | null;
  rawPharmaCompanyName?: string | null;
  normalizedVertices?: HighlightGeometryVertex[] | null;
  normalizedPolygons?: HighlightGeometryVertex[][];
  unmatched?: boolean;
  bounds?: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  };
}

const normalizeDocument = (raw: unknown): Record<string, unknown> | null => {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  if (Array.isArray(obj.pages)) return obj;
  const nested = obj.document;
  if (nested && typeof nested === 'object' && Array.isArray((nested as Record<string, unknown>).pages)) {
    return nested as Record<string, unknown>;
  }
  return (nested as Record<string, unknown>) ?? obj;
};

const fetchRawDocument = async (ocrRawUrl: string) => {
  const response = await fetch(ocrRawUrl, {
    signal: AbortSignal.timeout(20000),
  });
  if (!response.ok) {
    throw new Error(`OCR raw fetch HTTP ${response.status}`);
  }
  const bundle = await response.json();
  return normalizeDocument(bundle.document ?? bundle);
};

export const computeHighlightGeometry = async (input: {
  ocrRawUrl?: string | null;
  document?: unknown;
  drugs: GeometryDrugInput[];
  yearMonth?: string | null;
}): Promise<{
  rows: HighlightGeometryRow[];
  dimension?: { width: number; height: number };
  metadata: {
    drugCount: number;
    dataRowCount: number;
    tokenRowCount: number;
    unmatchedCount: number;
  };
}> => {
  const { ocrRawUrl, document, drugs, yearMonth } = input;

  if (!Array.isArray(drugs) || drugs.length === 0) {
    throw new Error('확정 약품 목록(drugs)이 필요합니다.');
  }
  if (!ocrRawUrl && !document) {
    throw new Error('ocrRawUrl 또는 document 중 하나가 필요합니다.');
  }

  let resolvedDocument: Record<string, unknown> | null = null;
  if (ocrRawUrl) {
    try {
      resolvedDocument = await fetchRawDocument(ocrRawUrl);
    } catch (error) {
      // S3 CORS 등으로 브라우저 fetch 실패 시 document 폴백을 호출자가 준비해야 함
      if (!document) {
        throw error instanceof Error
          ? error
          : new Error('OCR raw URL을 불러오지 못했습니다.');
      }
      resolvedDocument = normalizeDocument(document);
    }
  } else {
    resolvedDocument = normalizeDocument(document);
  }

  if (!resolvedDocument || !Array.isArray(resolvedDocument.pages)) {
    throw new Error('OCR document(pages)를 찾을 수 없습니다.');
  }

  const requestedYearMonth = /^\d{6}$/.test(String(yearMonth ?? ''))
    ? String(yearMonth)
    : null;

  const analysis = buildAnalysisRows({
    drugs,
    document: resolvedDocument,
    requestedYearMonth,
    prescriptionYearMonth: requestedYearMonth,
  }) as {
    rows: HighlightGeometryRow[];
    dimension?: { width: number; height: number };
    dataRowCount: number;
    tokenRowCount: number;
    unmatchedCount: number;
  };

  return {
    rows: analysis.rows,
    dimension: analysis.dimension,
    metadata: {
      drugCount: drugs.length,
      dataRowCount: analysis.dataRowCount,
      tokenRowCount: analysis.tokenRowCount,
      unmatchedCount: analysis.unmatchedCount,
    },
  };
};

export const toGeometryDrugs = (
  drugs: Array<{
    drugCode?: string | null;
    drugName?: string | null;
    pharmaCompanyName?: string | null;
    price?: number | null;
    outHospitalQuantity?: number | null;
    inHospitalQuantity?: number | null;
    totalAmount?: number | null;
  }>,
): GeometryDrugInput[] =>
  (drugs || []).map((drug) => ({
    drugCode: drug.drugCode ?? null,
    drugName: drug.drugName ?? null,
    pharmaCompanyName: drug.pharmaCompanyName ?? null,
    unitPrice: drug.price ?? null,
    outQuantity: drug.outHospitalQuantity ?? null,
    inQuantity: drug.inHospitalQuantity ?? null,
    totalAmount:
      drug.totalAmount ??
      (drug.price != null
        ? (drug.price ?? 0) *
          ((drug.outHospitalQuantity ?? 0) + (drug.inHospitalQuantity ?? 0))
        : null),
  }));
